import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SettingsChartImportSection } from "@/components/SettingsChartImportSection";
import { useCompany } from "@/context/CompanyContext";

/**
 * Verwaltet den Kontenrahmen einer Firma (CRUD + Edit-Panel).
 * Eigenständige Komponente; lädt accounts via /companies/{id}/accounts.
 */
export function SettingsAccountsSection({ companyId, isViewer, onAccountsChange }) {
  const { company } = useCompany();
  const [accounts, setAccounts] = useState([]);
  const [accountForm, setAccountForm] = useState({ code: "", name: "", type: "income", description: "" });
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingAccountForm, setEditingAccountForm] = useState({ name: "", type: "income", description: "" });

  const reload = () => {
    if (!companyId) return;
    apiClient
      .get(`/companies/${companyId}/accounts`)
      .then((r) => {
        setAccounts(r.data || []);
        onAccountsChange?.(r.data || []);
      })
      .catch(() => setAccounts([]));
  };
  useEffect(reload, [companyId, onAccountsChange]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!companyId || !accountForm.code || !accountForm.name) return;
    setAccountSaving(true);
    setAccountError("");
    try {
      const res = await apiClient.post(`/companies/${companyId}/accounts`, accountForm);
      const next = [...accounts, res.data];
      setAccounts(next);
      onAccountsChange?.(next);
      setAccountForm({ code: "", name: "", type: accountForm.type });
    } catch (e) {
      setAccountError(e.response?.data?.detail || "Konto konnte nicht angelegt werden.");
    } finally {
      setAccountSaving(false);
    }
  };

  const handleEdit = (acc) => {
    setEditingAccount(acc);
    setEditingAccountForm({
      name: acc.name,
      type: acc.type,
      description: acc.description || "",
      learned_notes: acc.learned_notes || [],
    });
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;
    try {
      const res = await apiClient.put(`/accounts/${editingAccount._id}`, {
        name: editingAccountForm.name,
        type: editingAccountForm.type,
        description: editingAccountForm.description,
        learned_notes: editingAccountForm.learned_notes || [],
      });
      const next = accounts.map((a) => (a._id === res.data._id ? res.data : a));
      setAccounts(next);
      onAccountsChange?.(next);
      setEditingAccount(null);
    } catch (e) {
      setAccountError(e.response?.data?.detail || "Konto konnte nicht aktualisiert werden.");
    }
  };

  const handleDelete = async (acc) => {
    setAccountError("");
    try {
      await apiClient.delete(`/accounts/${acc._id}`);
      const next = accounts.filter((a) => a._id !== acc._id);
      setAccounts(next);
      onAccountsChange?.(next);
    } catch (e) {
      setAccountError(e.response?.data?.detail || "Konto kann nicht gelöscht werden.");
    }
  };

  return (
    <Card
      id="section-kontenrahmen"
      className="p-4 border border-slate-200 bg-white shadow-sm scroll-mt-20"
      data-testid="settings-accounts-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-slate-700">Kontenrahmen</h2>
      </div>
      <SettingsChartImportSection companyId={companyId} isViewer={isViewer} onApplied={reload} undescribedCount={accounts.filter((a) => !a.description).length} setupDone={!!company?.setup_onboarding_done} />
      {accountError && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="accounts-error-banner"
        >
          {accountError}
        </div>
      )}
      <form
        className="flex flex-col md:flex-row md:items-end gap-3 mb-4"
        onSubmit={handleAdd}
        data-testid="accounts-form"
      >
        <div className="flex-1 space-y-1">
          <Label htmlFor="account-code">Kontonummer</Label>
          <Input
            id="account-code"
            value={accountForm.code}
            onChange={(e) => setAccountForm({ ...accountForm, code: e.target.value })}
            data-testid="accounts-code-input"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor="account-name">Kontoname</Label>
          <Input
            id="account-name"
            value={accountForm.name}
            onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
            data-testid="accounts-name-input"
          />
        </div>
        <div className="w-40 space-y-1">
          <Label htmlFor="account-type">Typ</Label>
          <Select
            value={accountForm.type}
            onValueChange={(value) => setAccountForm({ ...accountForm, type: value })}
          >
            <SelectTrigger id="account-type" data-testid="accounts-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asset">Aktiv</SelectItem>
              <SelectItem value="liability">Passiv</SelectItem>
              <SelectItem value="income">Ertrag</SelectItem>
              <SelectItem value="expense">Aufwand</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Button
            type="submit"
            size="sm"
            className="rounded-full bg-slate-900 text-slate-50 px-4"
            disabled={accountSaving || isViewer}
            data-testid="accounts-save-button"
          >
            {accountSaving ? "Speichern ..." : "Konto anlegen"}
          </Button>
        </div>
      </form>

      <Table data-testid="accounts-table">
        <TableHeader>
          <TableRow>
            <TableHead>Nr.</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Beschreibung (für KI-Vorschläge)</TableHead>
            <TableHead>Typ</TableHead>
            <TableHead className="w-24 text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.length === 0 && (
            <TableRow data-testid="accounts-empty-row">
              <TableCell
                colSpan={5}
                className="text-center text-sm text-slate-400"
                data-testid="accounts-empty-state"
              >
                Noch keine Konten angelegt.
              </TableCell>
            </TableRow>
          )}
          {accounts.map((acc) => (
            <TableRow key={acc._id} data-testid={`account-row-${acc._id}`}>
              <TableCell>{acc.code}</TableCell>
              <TableCell>{acc.name}</TableCell>
              <TableCell className="text-xs text-slate-500 max-w-[280px]">
                <div className="truncate" title={acc.description || ""}>
                  {acc.description || <span className="text-slate-300">—</span>}
                </div>
                {(acc.learned_notes || []).length > 0 && (
                  <div
                    className="mt-1 truncate rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[11px] text-violet-700"
                    title={acc.learned_notes.join(" • ")}
                    data-testid={`account-learned-notes-${acc._id}`}
                  >
                    Gelernt: {acc.learned_notes.join(" • ")}
                  </div>
                )}
              </TableCell>
              <TableCell className="capitalize">{acc.type}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(acc)}
                  disabled={isViewer}
                  data-testid={`account-edit-button-${acc._id}`}
                >
                  Bearbeiten
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(acc)}
                  disabled={isViewer}
                  data-testid={`account-delete-button-${acc._id}`}
                >
                  Löschen
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {editingAccount && (
        <div
          className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3"
          data-testid="edit-account-panel"
        >
          <div className="text-xs font-medium text-slate-600">
            Konto bearbeiten: {editingAccount.code}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="edit-account-name">Kontoname</Label>
              <Input
                id="edit-account-name"
                value={editingAccountForm.name}
                onChange={(e) =>
                  setEditingAccountForm({ ...editingAccountForm, name: e.target.value })
                }
                data-testid="edit-account-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-account-type">Typ</Label>
              <Select
                value={editingAccountForm.type}
                onValueChange={(value) =>
                  setEditingAccountForm({ ...editingAccountForm, type: value })
                }
              >
                <SelectTrigger
                  id="edit-account-type"
                  data-testid="edit-account-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset">Aktiv</SelectItem>
                  <SelectItem value="liability">Passiv</SelectItem>
                  <SelectItem value="income">Ertrag</SelectItem>
                  <SelectItem value="expense">Aufwand</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-account-description">Beschreibung — hilft dir & der KI beim Zuordnen</Label>
            <Input
              id="edit-account-description"
              value={editingAccountForm.description}
              onChange={(e) =>
                setEditingAccountForm({ ...editingAccountForm, description: e.target.value })
              }
              placeholder='z.B. "Software-Abos, Cloud-Dienste, Lizenzen (Emergent, Google, Payrexx-Software)"'
              data-testid="edit-account-description-input"
            />
          </div>
          {(editingAccountForm.learned_notes || []).length > 0 && (
            <div className="space-y-1" data-testid="edit-account-learned-section">
              <Label>Gelernt aus deinen Korrekturen (KI)</Label>
              <div className="space-y-1">
                {editingAccountForm.learned_notes.map((note, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 rounded border border-violet-200 bg-violet-50 px-2 py-1 text-xs text-violet-800"
                  >
                    <span className="min-w-0 truncate" title={note}>{note}</span>
                    <button
                      type="button"
                      className="text-violet-400 hover:text-violet-700 flex-shrink-0"
                      title="Lernregel entfernen"
                      onClick={() =>
                        setEditingAccountForm({
                          ...editingAccountForm,
                          learned_notes: editingAccountForm.learned_notes.filter((_, i) => i !== idx),
                        })
                      }
                      data-testid={`edit-account-learned-remove-${idx}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditingAccount(null)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              onClick={handleSaveEdit}
              disabled={isViewer}
            >
              Speichern
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
